import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api_call";
import "./Dashboard.css";

export default function ComplianceReport() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);

  const [data, setData] = useState([]);
 const [sortConfig, setSortConfig] = useState({
  key: "id",
  direction: "asc",
});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const rowsPerPage = 8;

  // ===================== FETCH DATA =====================
 useEffect(() => {
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  if (!isLoggedIn) {
    navigate("/", { replace: true });
    return;
  }

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await apiFetch("/report/admin/compliance", {
        method: "GET",
      });

      const records = res?.data || [];

      const formatted = records.map((row) => ({
  id: row.report_id,
  act: row.act,
  particular: row.name,
  title: row.title || "",
  description: row.description || "",
  startDate: row.start_date,
  actionDate: row.action_date,
  endDate: row.end_date,
  status: row.status,
  requestDate: row.request_date,
  responseDate: row.response_date,
  userName: row.user_name,
  approversEmail: row.approvers_email,

  bu: row.bu_name || "-",
  department: row.dept_name || "-"
}));

      setData(formatted);

    } catch (err) {
      console.error("Error fetching compliance report:", err);
      setError("Failed to fetch compliance report.");
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [navigate]);

  // ===================== SEARCH =====================
  const filteredData = useMemo(() => {
  if (!searchTerm) return data;

  const term = searchTerm.toLowerCase();

  return data.filter(
    (item) =>
      item.id?.toString().toLowerCase().includes(term) ||
      item.act?.toLowerCase().includes(term) ||
      item.particular?.toLowerCase().includes(term) ||
      item.title?.toLowerCase().includes(term)
  );
}, [data, searchTerm]);
  
// Add this useEffect inside ComplianceReport, before your return statement
useEffect(() => {
  const savedSearch = localStorage.getItem("searchitem") || "";
  const complianceId = localStorage.getItem("complianceId") || "";

  // Priority: complianceId (from chart click)
  if (complianceId.trim() !== "") {
    setSearchTerm(complianceId);
    localStorage.removeItem("complianceId");
  }
  // Otherwise use normal searchitem
  else if (savedSearch.trim() !== "") {
    setSearchTerm(savedSearch);
    // localStorage.removeItem("searchitem"); // optional
  }
}, []);

  const exportToCSV = () => {
  const headers = [
    "Id",
    "Compliance",
    "Particular",
    "Title",
    "Description",
    "Start Date",
    "Action Date",
    "End Date",
    "Status",
    "User",
    "BU",
    "Department",
    "Approver Email",
    "Request Date",
    "Response Date",
  ];

  const rows = data.map((r) => [
    r.id,
    r.act,
    r.particular,
    r.title,
    r.description,
    r.startDate,
    r.actionDate,
    r.endDate,
    r.status,
    r.userName,
    r.buName,
    r.deptName,
    r.approversEmail,
    r.requestDate,
    r.responseDate,
  ]);

  const csv =
    [headers, ...rows]
      .map((e) => e.map(v => `"${v ?? ""}"`).join(","))
      .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "compliance_report.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


  // ===================== SORT =====================
  const sortedData = useMemo(() => {
    let sortable = [...filteredData];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = (a[sortConfig.key] || "").toString().toLowerCase();
        const valB = (b[sortConfig.key] || "").toString().toLowerCase();
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };
  const statusCounts = useMemo(() => {
  const counts = {
    pending: 0,
    requested: 0,
    approved: 0,
    total: 0,
  };

  filteredData.forEach((item) => {
    counts.total++;

    const status = item.status?.toLowerCase();
    if (status === "pending") counts.pending++;
    else if (status === "requested") counts.requested++;
    else if (status === "approved") counts.approved++;
  });

  return counts;
}, [filteredData]);

  // ===================== PAGINATION =====================
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  
  // ===================== UI =====================
  return (
    <div className="general_">
      <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">Comprehensive Report</div>
      </div>

       <div className="table-header" style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: "10px", marginLeft: "45px" }}>
                <button className="btn gray-btn" onClick={() => navigate("/comprehensive")}>Comprehensive</button>
                <button className="btn blue-btn" onClick={() => navigate("/pending")}>Pending</button>
                <button className="btn blue-btn" onClick={() => navigate("/approved")}>Approved</button>
                <button className="btn blue-btn" onClick={() => navigate("/requested")}>Requested</button>
                <button className="btn blue-btn" onClick={() => navigate("/overdue")}>Overdue</button>
              </div>
      
              <div className="table-actions" style={{ display: "flex", gap: "15px", alignItems: "center" }}>

  {/* STATUS BADGES */}
  <div style={{ display: "flex", gap: "10px" }}>
    
    <div style={{
  background: "#e2e3e5",
  color: "#383d41",
  padding: "6px 12px",
  borderRadius: "20px",
  fontWeight: "600",
  fontSize: "13px"
}}>
  Total: {statusCounts.total}
</div>
    <div style={{
      background: "#f8d7da",
      color: "#721c24",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Pending: {statusCounts.pending}
    </div>

    <div style={{
      background: "#fff3cd",
      color: "#856404",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Requested: {statusCounts.requested}
    </div>

    <div style={{
      background: "#d4edda",
      color: "#155724",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Approved: {statusCounts.approved}
    </div>

  </div>

  {/* SEARCH */}
  <div className="search-box">
    <input
      placeholder="Search"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
    />
    <FaSearch className="search-icon" />
  </div>

  {/* EXPORT */}
  <button className="action-btn primary" onClick={exportToCSV}>
    Export
  </button>
</div>
            </div>

      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("id")}>Id {getSortIcon("id")}</th>
            <th onClick={() => requestSort("act")}>Compliance {getSortIcon("act")}</th>
            <th>Instance</th>
            <th onClick={() => requestSort("startDate")}>Start Date</th>
            <th onClick={() => requestSort("actionDate")}>Action Date</th>
            <th onClick={() => requestSort("endDate")}>End Date</th>
            <th onClick={() => requestSort("status")}>Status</th>
            <th>User</th>
            <th>Approver's Email</th>
            <th onClick={() => requestSort("requestDate")}>Request Date</th>
            <th onClick={() => requestSort("responseDate")}>Response Date</th>
          </tr>
        </thead>

        <tbody>
  {loading ? (
    <tr>
      <td colSpan="11" style={{ textAlign: "center", padding: "20px" }}>
        Loading records...
      </td>
    </tr>
  ) : error ? (
    <tr>
      <td colSpan="11" style={{ textAlign: "center", color: "red" }}>
        {error}
      </td>
    </tr>
  ) : currentRows.length > 0 ? (
    currentRows.map((row, index) => (
      <tr key={index}>
        <td>{row.id}</td>
        <td>{row.act}</td>

        {/* Instance + Description tooltip */}
      <td>
  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    <span>
      {row.title
        ? `${row.particular} - ${row.title}`
        : row.particular}
    </span>

    {row.description && (
      <IoIosInformationCircle
        title={row.description}
        style={{
          cursor: "pointer",
          fontSize: "16px",
          color: "#1A1744"
        }}
      />
    )}
  </div>
</td>

        <td>{row.startDate}</td>
        <td>{row.actionDate}</td>
        <td>{row.endDate}</td>

        <td
          style={{
            color:
              row.status?.toLowerCase() === "approved"
                ? "green"
                : row.status?.toLowerCase() === "requested"
                ? "#f0ad4e"
                : "red",
            fontWeight: 600,
          }}
        >
          {row.status}
        </td>

        {/* User with BU + Dept tooltip */}
       <td>
  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    <span>{row.userName}</span>

    {(row.bu || row.department) && (
      <IoIosInformationCircle
        title={`BU: ${row.bu} | Dept: ${row.department}`}
        style={{
          cursor: "pointer",
          fontSize: "16px",
          color: "#1A1744"
        }}
      />
    )}
  </div>
</td>

        <td>{row.approversEmail}</td>
        <td>{row.requestDate}</td>
        <td>{row.responseDate}</td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="11" style={{ textAlign: "center" }}>
        No records found
      </td>
    </tr>
  )}
</tbody>
      </table>

      <div className="pagination">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
          Prev
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
